import ArgumentWrapper from '@/components/arguments/ArgumentWrapper.jsx';
import Button from '@/components/Button.jsx';
import EnvironmentTag from '@/components/EnvironmentTag.jsx';
import Panel from '@/components/panel/Panel.jsx';
import { FiStar, IoSync } from '@/components/react-icons/main';
import Select from '@/components/Select.jsx';
import Tag from '@/components/Tag.jsx';
import Stack from '@/components/v3/Stack.jsx';
import credentialActions from '@/redux/actions/credentialActions.js';
import memoizeSelector from '@/redux/lib/memoizeSelector.js';
import selectors from '@/redux/selectors/selectors';
import workflowActions from '@/screens/workflows/redux/actions/workflowActions.js';
import workflowScreenSelectors from '@/screens/workflows/redux/selectors/workflowScreenSelectors';
import CredentialListItem from '@/screens/workflows/workflowEditor/workflowEditorPanels/primaryPanels/appPanel/appCredentialsPane/credentialList/CredentialListItem.jsx';
import { sortByProp } from '@shared/utils/sortByProp.js';
import { format } from 'date-fns';
import { useSelector } from 'react-redux';
import SliderClose from './SliderClose.jsx';
import SliderHeader from './SliderHeader.jsx';
import SliderTitle from './SliderTitle.jsx';

const EnvironmentAuthSlider = ({ onClose, disabled = false }) => {
  const appId = useSelector((state) => workflowScreenSelectors.activeWorkflowStepApp(state)?.id);
  const appIcon = useSelector((state) => workflowScreenSelectors.activeWorkflowStepApp(state)?.icon);
  const appLogo = useSelector((state) => workflowScreenSelectors.activeWorkflowStepApp(state)?.logo);
  const appName = useSelector((state) => workflowScreenSelectors.activeWorkflowStepApp(state)?.name);
  const companyId = useSelector(selectors.companyId);
  const environments = useSelector(memoizeSelector('environments', selectors.environments)) || [];
  const credentials = useSelector(memoizeSelector('companyCredentials', selectors.companyCredentials))?.filter((c) => c.appId === appId) || [];
  const appEnvironmentConnections = useSelector(memoizeSelector('activeWorkflowAppEnvironmentConnections', workflowScreenSelectors.activeWorkflowAppEnvironmentConnections));

  const credentialOptions = credentials
    ?.filter((n) => n.appId === appId)
    .sort(sortByProp(['valid', 'updatedOn'], ['desc', 'desc']))
    .map((n) => ({
      label: n.meta?.label || 'Unnamed',
      selectedLabel: n.meta?.label || 'Unnamed',
      value: n.credentialId,
      lastUpdated: n.updatedOn ? format(new Date(n.updatedOn), 'd MMM yy') : 'Unknown',
      status: n.status || 'unknown',
      statusAsOf: n.statusAsOf,
      type: n.meta?.type || 'unknown',
      usage: n.workflowCount || 0,
      environmentId: n.meta?.environmentId
    }));

  const selectOptions = credentialOptions?.map((n) => ({
    ...n,
    label: (
      <Stack gap={5}>
        <span>{n?.label}</span>
        {!!n?.environmentId && <EnvironmentTag environmentId={n?.environmentId} size={'xs'} />}
      </Stack>
    )
  }));

  const handleChange = (environmentId, credentialId) => {
    const credential = credentials?.find((n) => n?.credentialId === credentialId);
    if (credentialId && !credential?.meta?.environmentId) {
      credentialActions.set({
        appId,
        companyId,
        credentialId,
        valid: credential?.valid,
        isDefault: credential?.isDefault || false,
        secrets: { ...credential?.secrets, environmentId },
        meta: { ...credential?.meta, environmentId }
      });
    }

    workflowActions.setMetadata('appEnvironmentConnections', (prev = {}) => ({
      ...(prev || {}),
      [appId]: {
        ...(prev?.[appId] || {}),
        [environmentId]: credentialId
      }
    }));
  };

  const handleRemoveEnvironment = (environmentId) => {
    workflowActions.setMetadata('appEnvironmentConnections', (prev = {}) => ({
      ...(prev || {}),
      [appId]: {
        ...(prev?.[appId] || {}),
        [environmentId]: null
      }
    }));
  };

  const handleDelete = () => {
    workflowActions.setMetadata('appEnvironmentConnections', (prev = {}) => {
      const newState = { ...(prev || {}) };
      delete newState[appId];
      return newState;
    });
    onClose();
  };

  return (
    <>
      <SliderHeader onClose={onClose} showDelete={true} onDelete={handleDelete} disabled={disabled} />
      <SliderTitle icon={appLogo || appIcon || FiStar} title={`${appName} - Connections by Environment`} description={'Configure which connection to use for each environment.'} helpIconContext={'workflowEnvironmentConnections'} />

      {environments.map((environment) => (
        <Panel.Section key={environment.id} title={environment.name} rightContent={environment.isDefault && <Tag style={{ marginTop: 3 }}>Default</Tag>} collapseByDefault={false}>
          <ArgumentWrapper>
            {!appEnvironmentConnections?.[environment.id] && <Select disabled={disabled} options={selectOptions?.sort((a, b) => (b.environmentId === environment.id) - (a.environmentId === environment.id))} placeholder={`Select a ${environment.name} connection`} value={selectOptions?.find((n) => n?.value === appEnvironmentConnections[environment.id])?.selectedLabel} onChange={({ value }) => handleChange(environment.id, value)} />}
            {appEnvironmentConnections?.[environment.id] && <CredentialListItem credential={credentialOptions?.find((n) => n?.value === appEnvironmentConnections[environment.id])} isActive={true} hideDelete={true} disabled={disabled} />}
          </ArgumentWrapper>

          {appEnvironmentConnections?.[environment.id] && (
            <Button className={'full-width'} size={'xl'} icon={IoSync} onClick={() => handleRemoveEnvironment(environment.id, null)} disabled={disabled}>
              Change Connection
            </Button>
          )}
        </Panel.Section>
      ))}

      <SliderClose onClose={onClose} />
    </>
  );
};

export default EnvironmentAuthSlider;
